<template>
    <div class="category-management">
        <div class="category-header">
            <div class="header-content">
                <div class="header-left">
                    <h1 class="page-title">分类管理</h1>
                    <span class="page-subtitle">管理博客分类</span>
                </div>
                <div class="header-right">
                    <a-button type="primary" @click="showCreateDialog = true" :icon="h(PlusOutlined)">
                        新建分类
                    </a-button>
                </div>
            </div>
        </div>

        <div class="category-content">
            <!-- 加载状态 -->
            <a-spin v-if="isLoading" size="large" class="loading-spin">
                <div class="loading-content">
                    <p>正在加载分类列表...</p>
                </div>
            </a-spin>

            <!-- 错误状态 -->
            <a-result v-else-if="errorMessage" status="error" title="加载失败" :sub-title="errorMessage">
                <template #extra>
                    <a-button type="primary" @click="loadCategories">重试</a-button>
                </template>
            </a-result>

            <!-- 分类列表 -->
            <div v-else>
                <!-- 统计信息 -->
                <a-card class="stats-card" :bordered="false">
                    <a-statistic title="分类总数" :value="categories.length" />
                </a-card>

                <!-- 空状态 -->
                <a-empty v-if="categories.length === 0" description="还没有创建任何分类">
                    <a-button type="primary" @click="showCreateDialog = true">
                        创建第一个分类
                    </a-button>
                </a-empty>

                <!-- 分类列表 -->
                <a-row v-else :gutter="[16, 16]" class="categories-grid">
                    <a-col v-for="category in categories" :key="category.id" :xs="24" :sm="12" :md="8" :lg="6">
                        <a-card hoverable class="category-card">
                            <template #title>
                                <div class="category-header">
                                    <div class="category-title">{{ category.name }}</div>
                                    <div class="category-actions">
                                        <a-tooltip title="编辑分类">
                                            <a-button type="text" size="small" @click="editCategory(category)"
                                                class="action-btn">
                                                <EditOutlined />
                                            </a-button>
                                        </a-tooltip>
                                        <a-tooltip title="删除分类">
                                            <a-button type="text" size="small" danger @click="deleteCategory(category)"
                                                class="action-btn">
                                                <DeleteOutlined />
                                            </a-button>
                                        </a-tooltip>
                                    </div>
                                </div>
                            </template>

                            <div class="category-content">
                                <div class="category-description">
                                    {{ category.description || '暂无描述' }}
                                </div>
                                <div class="category-meta">
                                    <a-typography-text type="secondary">
                                        <ClockCircleOutlined />
                                        {{ formatDate(category.updated_at) }}
                                    </a-typography-text>
                                </div>
                            </div>
                        </a-card>
                    </a-col>
                </a-row>
            </div>
        </div>

        <!-- 创建/编辑分类模态框 -->
        <a-modal :open="showCreateDialog || showEditDialog" :title="isEditing ? '编辑分类' : '新建分类'" @ok="submitForm"
            @cancel="closeDialogs" :confirm-loading="isSubmitting" :ok-text="isEditing ? '保存修改' : '创建分类'"
            cancel-text="取消">
            <a-form :model="formData" layout="vertical">
                <a-form-item label="分类名称" name="name" :rules="[{ required: true, message: '请输入分类名称' }]">
                    <a-input v-model:value="formData.name" placeholder="请输入分类名称" :maxlength="50" show-count />
                </a-form-item>
                <a-form-item label="分类描述" name="description">
                    <a-textarea v-model:value="formData.description" placeholder="请输入分类描述（可选）" :maxlength="200"
                        :rows="3" show-count />
                </a-form-item>
            </a-form>
        </a-modal>

        <!-- 删除确认模态框 -->
        <a-modal :open="showDeleteDialog" title="确认删除" @ok="confirmDelete" @cancel="closeDialogs"
            :confirm-loading="isSubmitting" ok-text="确认删除" cancel-text="取消" ok-type="danger">
            <p>确定要删除分类 <strong>"{{ categoryToDelete?.name }}"</strong> 吗？</p>
            <a-alert message="此操作不可撤销！如果有文章使用该分类，则无法删除。" type="warning" show-icon class="delete-warning" />
        </a-modal>
    </div>
</template>

<script>
import { h } from 'vue'
import { message } from 'ant-design-vue'
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined
} from '@ant-design/icons-vue'
import supabaseCategoryService from '../../services/supabaseCategoryService.js'

export default {
    name: 'CategoryManagement',
    data() {
        return {
            categories: [],
            isLoading: true,
            errorMessage: null,

            // 对话框状态
            showCreateDialog: false,
            showEditDialog: false,
            showDeleteDialog: false,
            isEditing: false,
            isSubmitting: false,

            // 表单数据
            formData: {
                name: '',
                description: ''
            },

            // 编辑和删除相关
            editingCategory: null,
            categoryToDelete: null
        }
    },
    async mounted() {
        await this.loadData()
    },
    methods: {
        h,

        async loadData() {
            try {
                this.isLoading = true
                this.errorMessage = null

                const categories = await supabaseCategoryService.list()
                this.categories = categories
            } catch (error) {
                console.error('加载数据失败:', error)
                this.errorMessage = '加载数据失败，请刷新重试'
            } finally {
                this.isLoading = false
            }
        },

        async loadCategories() {
            await this.loadData()
        },

        // 编辑分类
        editCategory(category) {
            this.isEditing = true
            this.editingCategory = category
            this.formData = {
                name: category.name,
                description: category.description || ''
            }
            this.showEditDialog = true
        },

        // 删除分类
        deleteCategory(category) {
            this.categoryToDelete = category
            this.showDeleteDialog = true
        },

        // 提交表单
        async submitForm() {
            try {
                this.isSubmitting = true
                const operationName = this.isEditing ? '更新分类' : '创建分类'

                // 本地数据库操作
                if (this.isEditing) {
                    await supabaseCategoryService.update(this.editingCategory.id, this.formData)
                } else {
                    await supabaseCategoryService.create(this.formData)
                }

                // 重新加载本地数据
                await this.loadCategories()

                this.closeDialogs()
                message.success(`${operationName}成功！`)

            } catch (error) {
                console.error('操作失败:', error)
                message.error(`操作失败：${error.message}`)
            } finally {
                this.isSubmitting = false
            }
        },

        // 确认删除
        async confirmDelete() {
            try {
                this.isSubmitting = true

                await supabaseCategoryService.remove(this.categoryToDelete.id)

                // 重新加载本地数据
                await this.loadCategories()

                this.closeDialogs()
                message.success('分类删除成功！')

            } catch (error) {
                console.error('删除失败:', error)
                message.error(`删除失败：${error.message}`)
            } finally {
                this.isSubmitting = false
            }
        },

        // 关闭所有对话框
        closeDialogs() {
            this.showCreateDialog = false
            this.showEditDialog = false
            this.showDeleteDialog = false
            this.isEditing = false
            this.editingCategory = null
            this.categoryToDelete = null
            this.formData = {
                name: '',
                description: ''
            }
        },

        // 格式化日期
        formatDate(dateString) {
            return new Date(dateString).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }
    },

    components: {
        PlusOutlined,
        EditOutlined,
        DeleteOutlined,
        ClockCircleOutlined
    }
}
</script>

<style scoped>
.category-management {
    background: #f0f2f5;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.category-header {
    background: white;
    border-bottom: 1px solid #f0f0f0;
    margin-bottom: 0;
    width: 100%;
    max-width: 100%;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.header-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #262626;
}

.page-subtitle {
    color: #8c8c8c;
    font-size: 14px;
}

.header-right {
    display: flex;
    gap: 12px;
}

.category-content {
    padding: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.loading-spin {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 0;
}

.loading-content {
    text-align: center;
    margin-top: 16px;
}

.stats-card {
    margin-bottom: 24px;
    width: 100%;
    max-width: 100%;
}

.categories-grid {
    margin-top: 24px;
    width: 100%;
    max-width: 100%;
}

.category-card {
    height: 100%;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.category-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.category-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    margin-bottom: 8px;
}

.category-title {
    color: #1890ff;
    font-weight: 600;
    font-size: 16px;
    margin: 0;
    flex: 1;
}

.category-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
}

.category-card:hover .category-actions {
    opacity: 1;
}

.action-btn {
    padding: 4px !important;
    height: 24px !important;
    width: 24px !important;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
}

.action-btn:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

.category-content {
    margin-top: 0;
}

.category-description {
    color: #666;
    margin-bottom: 8px;
    line-height: 1.4;
    font-size: 14px;
}

.category-meta {
    margin-top: 0;
}

.category-meta .ant-typography {
    font-size: 12px;
    color: #999;
}

.delete-warning {
    margin-top: 16px;
}

/* 使用默认主题色 */
:deep(.ant-card-actions > li) {
    color: #666;
}

:deep(.ant-card-actions > li:hover) {
    color: #1890ff;
}

/* 重写Ant Design卡片的默认样式 */
.category-card :deep(.ant-card-head) {
    padding: 16px 16px 0 16px;
    border-bottom: none;
    min-height: auto;
}

.category-card :deep(.ant-card-body) {
    padding: 0 16px 16px 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .category-content {
        padding: 16px;
    }

    .stats-card {
        margin-bottom: 16px;
    }
}
</style>